import { Share2, SquareArrowOutUpRight } from "lucide-react";

import { ChatSaveTranscript } from "~/ai-chat/feature/chat-save-transcript";
import { ChatShareModal } from "~/ai-chat/feature/chat-share-modal";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useChatSaveShare } from "~/ai-chat/feature/use-chat-save-share";
import { chatWindowTitle } from "~/ai-chat/model/chat-window-title";
import type { ChatAction } from "~/ai-chat/ui/chat-action-group";

type ChatSaveShare = ReturnType<typeof useChatSaveShare>;

// The Save + Share entries for the conversation action group, shared by the floating widget and the
// full-page chat so the wiring lives in one place. Render `ChatSaveShareDialogs` with the returned
// `saveShare` alongside the group.
export function useChatSaveShareActions(): { actions: ChatAction[]; saveShare: ChatSaveShare } {
  const { messages, newChatPending } = useAiChat();
  const saveShare = useChatSaveShare();
  // Disabled until the first send binds a conversation: before then `activeConversationId` is null, so a
  // save can't link the transcript to the conversation and a later Share would duplicate it after a reload.
  const canSaveOrShare = messages.some((item) => item.kind === "message") && !newChatPending;

  return {
    saveShare,
    actions: [
      {
        key: "save",
        icon: SquareArrowOutUpRight,
        label: "Export chat to library",
        pressed: saveShare.saveDialogOpen,
        disabled: !canSaveOrShare,
        onClick: saveShare.openSaveDialog,
      },
      {
        key: "share",
        icon: Share2,
        label: "Share chat",
        pressed: saveShare.shareModalOpen,
        disabled: !canSaveOrShare,
        onClick: saveShare.openShareModal,
      },
    ],
  };
}

// The dialogs are portaled, so this can render anywhere.
export function ChatSaveShareDialogs({ saveShare }: { saveShare: ChatSaveShare }) {
  const { messages, chatTitle } = useAiChat();
  const defaultName = chatTitle ?? chatWindowTitle(messages);

  return (
    <>
      {saveShare.saveDialogOpen && (
        <ChatSaveTranscript messages={messages} defaultName={defaultName} onClose={saveShare.closeSaveDialog} />
      )}
      {saveShare.shareModalOpen && (
        <ChatShareModal
          messages={messages}
          defaultName={defaultName}
          initialResourceId={saveShare.shareInitialResourceId}
          onClose={saveShare.closeShareModal}
        />
      )}
    </>
  );
}
